// src/pages/driver-management/AddDriverScorePeriodicModal.jsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Grid, CircularProgress, Stack } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import FormDialog from '../../components/FormDialog';
import PickerField from '../../components/PickerField';
import { useCompanyDataList, useCompanyList } from '../../stores/companyStore';
import { useDriverList } from '../../stores/driverStore';
import { useDriverScoreConfigList } from '../../stores/driverScoreConfigStore';
import { createDriverScorePeriodic, updateDriverScorePeriodic, getDriverScorePeriodicById } from '../../stores/driverScorePeriodicStore';
import usePickerPagination from '../../hooks/usePickerPagination';
import { useNotifier } from '../../components/Notifier';
import { useLanguage } from '../../utils/i18n';

export default function AddDriverScorePeriodicModal({ open, id = null, onClose, onSuccess }) {
  const { t } = useLanguage();
  const notify = useNotifier();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [companyId, setCompanyId] = useState('');
  const [companyName, setCompanyName] = useState('');

  // Driver states
  const [driverId, setDriverId] = useState('');
  const [driverCode, setDriverCode] = useState('');
  const [driverName, setDriverName] = useState('');

  // Score Config states
  const [scoreConfigId, setScoreConfigId] = useState('');
  const [scoreConfigName, setScoreConfigName] = useState('');

  // Time states
  const [startTime, setStartTime] = useState(dayjs().startOf('day'));
  const [endTime, setEndTime] = useState(dayjs().endOf('day'));

  const [formErrors, setFormErrors] = useState({});

  // Company picker pagination
  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const { companyOptions } = useCompanyList();

  const combinedCompanyOptions = useMemo(() => {
    const list = companyPickerParams.options && companyPickerParams.options.length > 0 ? companyPickerParams.options : companyOptions;
    return (list || []).map((c) => ({
      value: String(c.value ?? c.id ?? ''),
      label: c.label ?? c.name ?? String(c.value ?? c.id ?? ''),
      raw: c._raw ?? c
    }));
  }, [companyPickerParams.options, companyOptions]);

  // Driver picker hook
  const driverPickerParams = usePickerPagination({
    fetchHook: useDriverList,
    buildArgs: (state) => [state.page, state.pageSize, companyId ? [{ value: String(companyId) }] : [], 'name', state.searchValue],
    searchKey: 'name',
    formatOption: (item) => ({
      value: String(item?.id ?? ''),
      label: item?.name ? `${item.name}${item.code ? ` (${item.code})` : ''}` : String(item?.id ?? ''),
      code: item?.code ?? '',
      name: item?.name ?? '',
      raw: item
    }),
    companyId
  });

  // Score Config picker hook (driver-score-config/search)
  const scoreConfigPickerParams = usePickerPagination({
    fetchHook: useDriverScoreConfigList,
    buildArgs: (state) => [null, state.page, state.pageSize, 'name', state.searchValue, companyId],
    searchKey: 'name',
    formatOption: (item) => ({
      value: String(item?.id ?? ''),
      label: item?.name ?? String(item?.id ?? ''),
      raw: item
    }),
    companyId
  });

  // Reset or initialize modal
  useEffect(() => {
    if (!open) {
      setFormErrors((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }

    let isMounted = true;
    const initForm = async () => {
      setFormErrors({});
      if (id) {
        setLoading(true);
        try {
          const data = await getDriverScorePeriodicById(id);
          if (!isMounted) return;
          if (data) {
            setCompanyId(String(data.company ?? data.company_id ?? ''));
            setCompanyName(data.company_name ?? '');
            setDriverId(String(data.driver ?? data.driver_id ?? ''));
            setDriverCode(data.driver_code ?? '');
            setDriverName(data.driver_name ?? '');
            setScoreConfigId(String(data.score_config ?? data.score_config_id ?? ''));
            setScoreConfigName(data.score_config_name ?? '');

            if (data.start_time) {
              setStartTime(dayjs(data.start_time));
            }
            if (data.end_time) {
              setEndTime(dayjs(data.end_time));
            }
          }
        } catch (err) {
          console.error('Failed to load score periodic detail:', err);
          notify({ message: t('notify.failedLoadData', 'Failed to load data'), severity: 'error' });
        } finally {
          if (isMounted) setLoading(false);
        }
      } else {
        // Defaults for Add mode
        let defaultCompId = '';
        let defaultCompName = '';
        try {
          const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
          if (stored && stored !== 'all') {
            defaultCompId = String(stored);
            const found = combinedCompanyOptions.find((c) => String(c.value) === String(stored));
            if (found) defaultCompName = found.label;
          }
        } catch {}

        setCompanyId(defaultCompId);
        setCompanyName(defaultCompName);
        setDriverId('');
        setDriverCode('');
        setDriverName('');
        setScoreConfigId('');
        setScoreConfigName('');
        setStartTime(dayjs().startOf('day'));
        setEndTime(dayjs().endOf('day'));
      }
    };

    initForm();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id, combinedCompanyOptions]);

  // Handle Driver Select
  const handleDriverSelect = (opt) => {
    if (!opt) {
      setDriverId('');
      setDriverCode('');
      setDriverName('');
      return;
    }
    const val = typeof opt === 'object' ? opt.value : opt;
    const raw = opt?.raw ?? opt;
    setDriverId(val != null ? String(val) : '');
    setDriverCode(opt?.code || raw?.code || '');
    setDriverName(opt?.name || raw?.name || opt?.label || '');
    if (formErrors.driver) setFormErrors((prev) => ({ ...prev, driver: null }));
  };

  // Handle Score Config Select
  const handleScoreConfigSelect = (opt) => {
    if (!opt) {
      setScoreConfigId('');
      setScoreConfigName('');
      return;
    }
    const val = typeof opt === 'object' ? opt.value : opt;
    const raw = opt?.raw ?? opt;
    setScoreConfigId(val != null ? String(val) : '');
    setScoreConfigName(opt?.label || raw?.name || '');
    if (formErrors.score_config) setFormErrors((prev) => ({ ...prev, score_config: null }));
  };

  // Form Submit Handler
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const errors = {};
    if (!companyId) errors.company = t('validation.companyRequired', 'Company is required.');
    if (!driverId) errors.driver = t('validation.driverRequired', 'Driver is required.');
    if (!scoreConfigId) errors.score_config = t('validation.scoreConfigRequired', 'Score config is required.');

    if (!startTime || !startTime.isValid()) {
      errors.start_time = t('validation.startRequired', 'Start time is required.');
    }
    if (!endTime || !endTime.isValid()) {
      errors.end_time = t('validation.endRequired', 'End time is required.');
    } else if (startTime && startTime.isValid() && !endTime.isAfter(startTime)) {
      errors.end_time = t('validation.endTimeAfterStart', 'End time must be after start time.');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      notify({ message: t('driverScore.formInvalid', 'Please check the form for errors.'), severity: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        company: Number(companyId),
        company_name: companyName || '',
        driver: Number(driverId),
        driver_code: driverCode || '',
        driver_name: driverName || '',
        score_config: Number(scoreConfigId),
        score_config_name: scoreConfigName || '',
        end_time: endTime.subtract(7, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]'),
        start_time: startTime.subtract(7, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]')
      };

      if (id) {
        await updateDriverScorePeriodic(id, payload);
        notify({ message: t('notify.update', 'Updated successfully'), severity: 'success' });
      } else {
        await createDriverScorePeriodic(payload);
        notify({ message: t('notify.create', 'Created successfully'), severity: 'success' });
      }

      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (err) {
      console.error('Submit score periodic error:', err);
      const errMsg = err?.response?.data?.message || err?.message || t('notify.saveFailed', 'Failed to save data');
      notify({ message: errMsg, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const dialogActions = (
    <Stack direction="row" spacing={1.5} justifyContent="flex-end">
      <Button variant="outlined" onClick={onClose} disabled={submitting}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={submitting || loading}
        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{
          textTransform: 'none',
          borderRadius: 1,
          px: 3,
          fontWeight: 700,
          boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
        }}
      >
        {submitting ? t('buttonTitle.processing', 'Processing...') : id ? t('buttonTitle.save', 'Save') : t('buttonTitle.add', 'Add')}
      </Button>
    </Stack>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormDialog
        open={open}
        onClose={onClose}
        title={id ? `${t('general.edit', 'Edit')} Driver Score Periodic` : `${t('general.add', 'Add')} Driver Score Periodic`}
        maxWidth="sm"
        actions={dialogActions}
      >
        <Box sx={{ width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <Grid container spacing={2.5}>
                {/* Company Picker */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <PickerField
                    label={`${t('general.company', 'Company')} *`}
                    value={companyId}
                    {...companyPickerParams}
                    options={combinedCompanyOptions}
                    onChange={(opt) => {
                      const val = typeof opt === 'object' ? opt?.value : opt;
                      const label = typeof opt === 'object' ? opt?.label : '';
                      setCompanyId(val != null ? String(val) : '');
                      setCompanyName(label || opt?.name || '');
                      // Reset dependent fields on company change
                      setDriverId('');
                      setDriverCode('');
                      setDriverName('');
                      setScoreConfigId('');
                      setScoreConfigName('');
                      if (formErrors.company) setFormErrors((prev) => ({ ...prev, company: null }));
                    }}
                    dialogTitle={t('masterCountry.selectCompany', 'Select Company')}
                    error={!!formErrors.company}
                    helperText={formErrors.company}
                  />
                </Grid>

                {/* Driver Picker */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <PickerField
                    label={`${t('masterDriver.driver', 'Driver')} *`}
                    value={driverId}
                    {...driverPickerParams}
                    onChange={handleDriverSelect}
                    dialogTitle={t('general.selectDriver', 'Select Driver')}
                    error={!!formErrors.driver}
                    helperText={formErrors.driver}
                    disabled={!companyId}
                  />
                </Grid>

                {/* Score Config Picker */}
                <Grid size={{ xs: 12 }}>
                  <PickerField
                    label={`${t('driverScore.scoreConfig', 'Score Config')} *`}
                    value={scoreConfigId}
                    {...scoreConfigPickerParams}
                    onChange={handleScoreConfigSelect}
                    dialogTitle={t('driverScore.selectScoreConfig', 'Select Score Config')}
                    error={!!formErrors.score_config}
                    helperText={formErrors.score_config}
                    disabled={!companyId}
                  />
                </Grid>

                {/* Start Time */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <DateTimePicker
                    label={`${t('driverScore.startTime', 'Start Time')} *`}
                    value={startTime}
                    onChange={(newValue) => {
                      setStartTime(newValue);
                      if (formErrors.start_time) setFormErrors((prev) => ({ ...prev, start_time: null }));
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!formErrors.start_time,
                        helperText: formErrors.start_time
                      }
                    }}
                    ampm={false}
                    timeSteps={{ minutes: 1 }}
                  />
                </Grid>

                {/* End Time */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <DateTimePicker
                    label={`${t('driverScore.endTime', 'End Time')} *`}
                    value={endTime}
                    onChange={(newValue) => {
                      setEndTime(newValue);
                      if (formErrors.end_time) setFormErrors((prev) => ({ ...prev, end_time: null }));
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!formErrors.end_time,
                        helperText: formErrors.end_time
                      }
                    }}
                    ampm={false}
                    timeSteps={{ minutes: 1 }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </FormDialog>
    </LocalizationProvider>
  );
}

AddDriverScorePeriodicModal.propTypes = {
  open: PropTypes.bool.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};
